import { Modal, Spin, message } from 'antd';
import { createElement } from 'react';
import ExclamationCircleFilled from '@ant-design/icons/ExclamationCircleFilled';
import { Tree } from 'tree-wallet-client';
import * as TreeRepository from '../Repository/TreeRepository';
import ConfigurationService from './ConfigurationService';
import { claimTreesListCopy, compactTreesListCopy, endTreesListCopy, isNativeApp, mergeTrees, releaseTreesListCopy, writeGeoJsonFile } from '../Dependency/NativeAppMemory';
import { treesListVersion, writeTreesListVersion } from './MapTreesListService';
import { NO_NETWORK_CONNECTIVITY, NO_TREES_TO_SAVE, OFFLINE_SAVE_ALREADY_RUNNING, OFFLINE_SAVE_APP_ONLY, TREES_LIST_SIZE_UNREADABLE, offlineSaveDeviceFull, offlineSaveFailed, offlineSavePhotosFailed, offlineSaved } from '../Helper/BannerMessages';

// Rows read per request: the same page size the list uses, so the trees list copy and the map read the same way
const LIST_PAGE_SIZE = 500;
const PHOTO_CONCURRENCY = 6;

// The size the API compresses a photo to (`PhotoController::OFFLINE_PHOTO_TARGET_BYTES`): 1024 px, JPEG
export const OFFLINE_PHOTO_TARGET_BYTES = 50 * 1024;

// Past this much the photos stop being kept: what the cache holds, not what comes over the network
const OFFLINE_PHOTO_CAP = 200000 * 1024 * 1024;  // 2GB

/**
 * What one tree's row in the list costs (~2 KB), and what one photo costs: 
 * `Save Offline` keeps the API's compressed version of a photo (~OFFLINE_PHOTO_TARGET_BYTES)
 */
const AVERAGE_TREE_ROW_BYTES = 2 * 1024;
// Rows read to count the distinct photo files: enough for a rate, without a full page on the API
const ESTIMATE_SAMPLE_SIZE = 100;

// The compressed photos `Save Offline` keeps, answered by the service worker only when there is no network
const OFFLINE_PHOTO_CACHE = 'image-offline-cache';


// The filters the trees list copy was saved with, kept beside it: offline the map opens on the very list that was saved
const SAVED_COLLECTION_KEY = 'tw.offlineFilter';

export interface SavedCollection {
  filter: any;
  contractId?: number;
  savedAt: number;
  trees: number;
  photos: number;
  bytes: number;
}

export function readSavedCollection(): SavedCollection | null {
  try {
    const raw = localStorage.getItem(SAVED_COLLECTION_KEY);

    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeSavedCollection(collection: SavedCollection): void {
  try {
    localStorage.setItem(SAVED_COLLECTION_KEY, JSON.stringify(collection));
  } catch (error) {
    console.warn('could not remember the filters the trees were saved with', error);
  }
}

export interface OfflineSaveProgress {
  treesDone: number;
  trees: number;
  bytes: number;
  expectedBytes: number;
}

export interface OfflineSaveRequest {
  filter: any;
  admin: boolean;
  contractId?: number;
}

interface OfflineEstimate {
  trees: number;
  /** Trees planted, the figure the map's counter shows: a record can hold several of them */
  planted: number;
  photos: number;
  bytes: number;
  /** What one photo counts as while downloading: nothing readable tells its size (see `savePhoto`) */
  photoNominalBytes: number;
}

let progress: OfflineSaveProgress | null = null;
const listeners = new Set<(state: OfflineSaveProgress | null) => void>();

// A save reports once per photo: the overlay does not need more than ten frames a second
const REPORT_INTERVAL_MS = 100;
let lastReportAt = 0;

const report = (next: OfflineSaveProgress | null) => {
  progress = next;

  const now = Date.now();
  if (next && now - lastReportAt < REPORT_INTERVAL_MS) {
    return;
  }

  lastReportAt = now;
  listeners.forEach((listener) => listener(next));
};

/** The overlay follows the job: it is shown while the estimate is read and while the trees list copy is written */
export function subscribeOfflineSave(listener: (state: OfflineSaveProgress | null) => void): () => void {
  listeners.add(listener);
  listener(progress);

  return () => {
    listeners.delete(listener);
  };
}

export function formatDownload(bytes: number): string {
  if (!bytes) {
    return '0 MB';
  }

  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

/** The downloaded figure while saving: two decimals, so every photo visibly moves it */
export function megabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const absolutePhotoUrl = (url: string): string => {
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }

  const base = ConfigurationService.urls().baseApiUrl;
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
};

const photoUrlsOf = (trees: Tree[]): string[] => {
  const urls = new Set<string>();

  trees.forEach((tree) => {
    (tree.photos || []).forEach((photo) => {
      if (photo?.imageUrl) {
        urls.add(photo.imageUrl);
      }
    });
  });

  return [...urls];
};

/** The API's compressed version of a photo: what `Save Offline` keeps, under the photo's own url */
const offlinePhotoUrl = (url: string): string | null => {
  try {
    const base = ConfigurationService.urls().baseApiUrl;
    const name = new URL(url, base).pathname.split('/').pop();

    return name ? `${base}/api/photo/offline/${encodeURIComponent(name)}` : null;
  } catch (error) {
    return null;
  }
};

/** The page the list shows, and its total */
const readPage = (request: OfflineSaveRequest, page: number, pageSize: number = LIST_PAGE_SIZE) =>
  TreeRepository.getTreeList({
    queryParams: { filter: JSON.stringify(request.filter), pageSize, page },
    admin: request.admin,
  });

/**
 * What the dialog asks about: the number of trees, and the photo files they share - counted on one page and
 * extrapolated, because a list where a thousand trees show the same picture does not cost a thousand photos.
 */
async function estimateOfflineSave(request: OfflineSaveRequest): Promise<OfflineEstimate> {
  const first = await withDeadline(readPage(request, 1, ESTIMATE_SAMPLE_SIZE), PAGE_TIMEOUT_MS, 'trees list');
  const rows: Tree[] = first?.data ?? [];
  const trees = Number(first?.pager?.totalRecords ?? 0);
  const planted = Number(first?.extraData?.treeCount ?? trees);

  const photoFiles = photoUrlsOf(rows).length;
  const photosPerTree = rows.length ? photoFiles / rows.length : 0;
  const photos = Math.round(photosPerTree * trees);

  return {
    trees,
    planted,
    photos,
    bytes: trees * AVERAGE_TREE_ROW_BYTES + photos * OFFLINE_PHOTO_TARGET_BYTES,
    photoNominalBytes: OFFLINE_PHOTO_TARGET_BYTES,
  };
}

// One more attempt for a page or a photo, and how long to wait: a save of thousands of requests meets a few
const SAVE_ATTEMPTS = 2;
const SAVE_RETRY_DELAY = 500;

// A connection that stalls never rejects on its own: without a deadline one photo freezes its worker for good
const PHOTO_TIMEOUT_MS = 30_000;
const PAGE_TIMEOUT_MS = 45_000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function timeoutSignal(ms: number): AbortSignal | undefined {
  const timeout = (AbortSignal as any)?.timeout;

  return typeof timeout === 'function' ? timeout(ms) : undefined;
}

/** A request that answers or gives up: a stalled page must not hold the whole save */
function withDeadline<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** The page the list shows, given a second chance: one dropped request must not end a save of hundreds */
async function readPageWithRetry(request: OfflineSaveRequest, page: number) {
  for (let attempt = 1; ; attempt++) {
    try {
      return await withDeadline(readPage(request, page), PAGE_TIMEOUT_MS, `page ${page}`);
    } catch (error) {
      if (attempt === SAVE_ATTEMPTS) {
        throw error;
      }

      await wait(SAVE_RETRY_DELAY);
    }
  }
}

interface PhotoOutcome {
  /** What the cache holds once this photo is in: what the size cap counts */
  bytes: number;
  /** What came over the network: the figure the progress bar follows */
  downloaded: number;
}

/**
 * The API's compressed version of a photo, the one `Save Offline` is meant to keep. `null` when the server has
 * none to offer, which is what sends the caller back to the uploaded file.
 */
async function keepCompressedPhoto(url: string, cache: Cache): Promise<(PhotoOutcome & { failed?: boolean }) | null> {
  const variant = offlinePhotoUrl(url);

  if (!variant) {
    return null;
  }

  const response = await fetch(variant, {
    cache: 'no-store',
    signal: timeoutSignal(PHOTO_TIMEOUT_MS),
  });

  if (!response.ok) {
    return null;
  }

  const blob = await response.blob();
  await cache.put(url, new Response(blob, {
    // Informs the worker of the compressed photo
    headers: { 'Content-Type': 'image/jpeg', 'X-Offline-Photo': 'compressed' },
  }));

  // Stored, or not there at all: a photo the browser silently refused is a photo the popup will miss
  if (!(await cache.match(url))) {
    return { bytes: 0, downloaded: blob.size, failed: true };
  }

  return { bytes: blob.size, downloaded: blob.size };
}

/**
 * The uploaded file itself, for a server that serves no compressed version. Another origin (`/images` is served
 * without CORS headers) is read in `no-cors`: the answer cannot be inspected or measured, but it can be kept -
 * which is all the popup and the viewer need offline - so it is weighed with the sample's average instead.
 */
async function keepFullPhoto(url: string, cache: Cache, nominalBytes: number): Promise<PhotoOutcome & { failed?: boolean }> {
  let readable = false;
  try {
    readable = new URL(url).origin === window.location.origin;
  } catch (error) {
    readable = true;
  }

  const response = await fetch(url, {
    cache: 'no-store',
    signal: timeoutSignal(PHOTO_TIMEOUT_MS),
    ...(readable ? {} : { mode: 'no-cors' as RequestMode }),
  });

  if (response.type === 'opaque') {
    await cache.put(url, response);

    if (!(await cache.match(url))) {
      return { bytes: 0, downloaded: 0, failed: true };
    }

    return { bytes: nominalBytes, downloaded: nominalBytes };
  }

  if (!response.ok) {
    return { bytes: 0, downloaded: 0, failed: true };
  }

  const blob = await response.blob();
  await cache.put(url, new Response(blob, { headers: { 'Content-Type': blob.type || 'image/jpeg' } }));

  return { bytes: blob.size, downloaded: blob.size };
}

/**
 * One photo into the cache, compressed when the API offers it. A full device and a single dead photo are told
 * apart: the first stops the photos (the trees still get saved), the second is counted and skipped. What a
 * previous attempt already saved is not fetched again, so pressing the link once more resumes instead of
 * starting over.
 */
async function savePhoto(url: string, cache: Cache | null, nominalBytes: number): Promise<PhotoOutcome & { failed?: boolean; full?: boolean }> {
  if (!cache) {
    return { bytes: 0, downloaded: 0 };
  }

  try {
    if (await cache.match(url)) {
      return { bytes: 0, downloaded: 0 };
    }
  } catch (error) {
    // A cache that cannot be read is no reason to stop: the fetch below still fills it
  }

  for (let attempt = 1; ; attempt++) {
    try {
      return (await keepCompressedPhoto(url, cache))
        ?? (await keepFullPhoto(url, cache, nominalBytes));
    } catch (error) {
      const name = String((error as any)?.name || '');
      if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') {
        return { bytes: 0, downloaded: 0, full: true };
      }

      if (attempt === SAVE_ATTEMPTS) {
        return { bytes: 0, downloaded: 0, failed: true };
      }

      await wait(SAVE_RETRY_DELAY);
    }
  }
}

/**
 * The whole list, its photos and full single trees data to be saved offline on the device
 */
async function saveOffline(request: OfflineSaveRequest, estimate: OfflineEstimate): Promise<void> {
  const state: OfflineSaveProgress = {
    treesDone: 0,
    trees: estimate.trees,
    bytes: 0,
    expectedBytes: estimate.bytes,
  };

  // The dialog has just closed: the overlay takes over at once, without waiting for the trees list copy claim
  report({ ...state });

  if (!(await claimTreesListCopy())) {
    report(null);
    message.warning(OFFLINE_SAVE_ALREADY_RUNNING);
    return;
  }

  // A page left mid-save has to hand the trees list copy back, or the next save waits for the claim to expire
  const releaseOnHide = () => void releaseTreesListCopy();
  window.addEventListener('pagehide', releaseOnHide);

  try {
    const photoCache = typeof caches !== 'undefined' ? await caches.open(OFFLINE_PHOTO_CACHE) : null;

    let photoBytes = 0;
    let treeCount = 0;
    let photosFailed = 0;
    let photosStopped = false;
    // A photo file is downloaded once, however many trees show it
    const savedPhotos = new Set<string>();

    /**
     * The total is the one the dialog asked about, and it stays that: the figures above and below the bar must
     * describe the same download, so the estimated size is not refined behind the user's back.
     */
    for (let page = 1; ; page++) {
      const response = await readPageWithRetry(request, page);
      const rows: Tree[] = response?.data ?? [];

      if (!rows.length) {
        break;
      }

      await mergeTrees(rows);

      state.bytes += rows.reduce((total, row) => total + JSON.stringify(row).length, 0);
      report({ ...state });

      const urls = photoUrlsOf(rows).filter((url) => !savedPhotos.has(url));
      urls.forEach((url) => savedPhotos.add(url));
      let cursor = 0;

      const worker = async () => {
        while (cursor < urls.length && !photosStopped && photoBytes < OFFLINE_PHOTO_CAP) {
          const url = urls[cursor++];

          // The compressed photo the API serves for offline use
          const photo = await savePhoto(absolutePhotoUrl(url), photoCache, estimate.photoNominalBytes);
          photosStopped = photosStopped || !!photo.full;
          photosFailed += photo.failed ? 1 : 0;
          photoBytes += photo.bytes;
          state.bytes += photo.downloaded;

          report({ ...state });
        }
      };

      await Promise.all(Array.from({ length: Math.min(PHOTO_CONCURRENCY, urls.length) }, worker));

      // A page is done once its photos are: the bar must not reach the end while they are still downloading
      treeCount += rows.length;
      state.treesDone = treeCount;
      report({ ...state });

      if (rows.length < LIST_PAGE_SIZE) {
        break;
      }
    }

    // The map data: the same files the map reads, stored where it reads them from. 
    // A failure here leaves the trees list copy saved, which is what the filters need
    let version: string | null = null;

    if (request.contractId) {
      try {
        const list = await TreeRepository.getGeoJsons({ params: { contractId: request.contractId } });

        // A file that could not be read would leave a hole in the stored set, so the version is only written
        // when the whole set arrived: a partial set must stay "not current" and be downloaded again
        let filesFailed = 0;

        for (const file of list?.files || []) {
          // Marked like the map's own download: the worker must not keep a second copy of what the store holds
          const url = `${ConfigurationService.urls().baseApiUrl}${file.url}`;
          const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}tw=copy`, { cache: 'no-store' });
          if (!response.ok) {
            filesFailed += 1;
            console.warn('a map file could not be saved offline', { url, status: response.status });
            continue;
          }

          const text = await response.text();
          state.bytes += text.length;
          report({ ...state });

          await writeGeoJsonFile(file.url, text);
        }

        if (!filesFailed) {
          version = treesListVersion(list || {});
          writeTreesListVersion(String(request.contractId), version);
        }
      } catch (error) {
        console.warn('could not save the map data offline', { error });
      }
    }

    // The trees list copy is now complete so this is what lets an offline filter and the Calendar answer at all (see `readTreesListCopyPage`)
    await endTreesListCopy();

    // `Save Offline` only adds to filtered rows the corresponding photos + full single trees data)
    writeSavedCollection({
      filter: request.filter,
      contractId: request.contractId,
      savedAt: Date.now(),
      trees: state.treesDone,
      photos: savedPhotos.size,
      bytes: state.bytes,
    });

    // The save wrote the whole trees list copy through the write-ahead log: truncate it, or the file keeps a few MB
    await compactTreesListCopy();

    const saved = offlineSaved(state.treesDone, formatDownload(state.bytes));

    if (photosStopped) {
      message.warning(offlineSaveDeviceFull(saved));
    } else if (photosFailed > 0) {
      message.warning(offlineSavePhotosFailed(saved, photosFailed));
    } else {
      message.success(saved);
    }
  } catch (error) {
    console.error('could not save the trees offline', { error });
    const reason = String((error as any)?.message || '').trim();
    message.error(offlineSaveFailed(reason));
  } finally {
    window.removeEventListener('pagehide', releaseOnHide);
    await releaseTreesListCopy();
    report(null);
  }
}

/**
 * Reads the estimate, asks the user, then saves: the only path into the offline trees list copy.
 */
export async function askAndSaveOffline(request: OfflineSaveRequest): Promise<void> {
  if (!isNativeApp()) {
    message.info(OFFLINE_SAVE_APP_ONLY);
    return;
  }

  if (!navigator.onLine) {
    message.warning(NO_NETWORK_CONNECTIVITY);
    return;
  }

  // The dialog opens at once and works the figure out in place: the user sees what is being asked,
  // and answers only once the MB size to be downloadedis known
  const answer: { estimate?: OfflineEstimate } = {};
  let cancelled = false;

  const confirm = Modal.confirm({
    title: 'Save Offline',
    icon: createElement(Spin, { size: 'small' }),
    content: 'Working out how much there is to download…',
    okText: 'Yes',
    cancelText: 'No',
    okButtonProps: { disabled: true },
    centered: true,
    // The dialog closes on the answer: nothing is awaited here, the overlay reports dynamically the downloading status
    onOk: () => {
      if (answer.estimate) {
        void saveOffline(request, answer.estimate);
      }
    },
    // Saying No while the size is still being worked out is an answer too: nothing is saved
    onCancel: () => {
      cancelled = true;
    },
  });

  try {
    answer.estimate = await estimateOfflineSave(request);
  } catch (error) {
    console.error('could not work out what saving offline costs', { error });
    confirm.destroy();
    message.error(TREES_LIST_SIZE_UNREADABLE);
    return;
  }

  if (cancelled) {
    return;
  }

  const estimate = answer.estimate;

  if (!estimate.trees) {
    confirm.destroy();
    message.info(NO_TREES_TO_SAVE);
    return;
  }

  // One record per row, several trees planted in it: the map's counter adds them up, the download counts the rows
  const records = estimate.planted > estimate.trees ? `${estimate.trees} tree records` : `${estimate.trees} trees`;
  const planted = estimate.planted > estimate.trees ? `, ${estimate.planted} trees planted` : '';
  const photos = estimate.photos
    ? `, ${estimate.photos} photo${estimate.photos > 1 ? 's' : ''}`
    : '';

  confirm.update({
    icon: createElement(ExclamationCircleFilled),
    content: `Roughly ${formatDownload(estimate.bytes)} will be downloaded - `
      + `${records}${planted}${photos}. Do you want to continue?`,
    okButtonProps: { disabled: false },
  });
}
